<template>
  <div>
    <v-card
      height="64px"
      width="100%"
      class="background ml-4 mr-6 my-0 px-4 transparent elevation-0 d-flex flex-row justify-space-between align-center"
      style="position: fixed; z-index: 200; top: 0; width: 98%; padding-top: 16px;"
    >
      <v-card-title class="ml-0 pl-0">
        <p
          class="breadcrumb-text underline-on-hover cursor"
          @click="$router.push('/question-bank')"
        >
          Question Bank
        </p>
        <v-icon class="breadcrumb-arrow-unselected" style="margin-top: 6px !important;">
          mdi-chevron-right
        </v-icon>
        <p class="breadcrumb-text">View Question</p>
      </v-card-title>
      <UserNavMenu />
    </v-card>

    <v-container fluid class="question-view-page px-6 py-0">
      <div class="question-view-card pa-6">
        <div v-if="isLoading" class="question-view-loading">
          <v-progress-circular indeterminate color="primary" :size="28" :width="3" />
          <span>Loading question...</span>
        </div>

        <template v-else-if="isPassageBased">
          <div class="question-view-section">
            <p class="question-view-label mb-2">Question Type</p>
            <p class="question-view-value mb-0">
              {{ formatQuestionType(questionDetail.questionType) || "NA" }}
            </p>
          </div>

          <div class="question-view-section mt-8">
            <p class="question-view-label mb-2">Passage</p>
            <div
              class="question-view-rich-text"
              v-html="formatQuestionStatementForDisplay(questionDetail.questionStatement) || 'NA'"
            ></div>
          </div>

          <div class="question-view-meta-grid mt-8">
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Application</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.application) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Skill</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.skill) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Set Number</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.setNumber) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">NEP Level</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.level) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Grade</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.grade) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Subject</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.subject) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">HRIS Unique Role</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.hrisUniqueRole) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Language</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.languageOfQuestion) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Authored By</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.authoredBy) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Group</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.groupName) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Customer</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.customer) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Difficulty Level</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.difficultyLevel) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Number of Questions</p>
              <p class="question-view-value mb-0">{{ getDisplayValue((questionDetail.subQuestions || []).length) }}</p>
            </div>
          </div>

          <div class="question-view-section mt-8">
            <div class="d-flex align-center justify-space-between mb-4">
              <p class="question-view-section-title mb-0">Questions</p>
              <div class="d-flex align-center">
                <v-btn
                  class="btn outline-btn mr-3"
                  :disabled="!(questionDetail.subQuestions || []).length || viewQuestionPanels.length === (questionDetail.subQuestions || []).length"
                  @click="toggleViewQuestionPanels(true)"
                >
                  Expand All
                </v-btn>
                <v-btn
                  class="btn outline-btn"
                  :disabled="!viewQuestionPanels.length"
                  @click="toggleViewQuestionPanels(false)"
                >
                  Collapse All
                </v-btn>
              </div>
            </div>
            <v-expansion-panels v-model="viewQuestionPanels" multiple flat>
              <v-expansion-panel
                v-for="(subQuestion, index) in questionDetail.subQuestions || []"
                :key="index"
                class="question-view-expansion-panel"
              >
                <v-expansion-panel-header class="question-view-expansion-header">
                  Question {{ index + 1 }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="question-view-section pt-4">
                    <p class="question-view-label mb-2">Question Type</p>
                    <p class="question-view-value mb-0">
                      {{ formatQuestionType(subQuestion.questionType) || "NA" }}
                    </p>
                  </div>

                  <div class="question-view-section mt-8">
                    <p class="question-view-label mb-2">Question</p>
                    <div
                      class="question-view-rich-text"
                      v-html="formatQuestionStatementForDisplay(subQuestion.questionStatement) || 'NA'"
                    ></div>
                  </div>

                  <div class="question-view-summary-grid mt-8">
                    <div>
                      <p class="question-view-label mb-2">Marking Type</p>
                      <p class="question-view-value mb-0">{{ subQuestion.markingType || "NA" }}</p>
                    </div>
                    <div>
                      <p class="question-view-label mb-2">Correct Answer Score</p>
                      <p class="question-view-value mb-0">
                        {{ subQuestion.correctAnswerScore ?? "NA" }}
                      </p>
                    </div>
                  </div>

                  <div
                    v-if="subQuestion.questionType !== 'MATCH_THE_FOLLOWING' && subQuestion.questionType !== 'FILL_IN_THE_BLANKS' && getSubQuestionResolvedOptions(subQuestion).length"
                    class="question-view-section mt-8"
                  >
                    <p class="question-view-section-title mb-4">Options</p>
                    <div class="question-view-options-grid">
                      <div
                        v-for="(option, optionIndex) in getSubQuestionResolvedOptions(subQuestion)"
                        :key="option.optionKey || optionIndex"
                        class="question-view-option-item"
                      >
                        <span
                          class="question-view-option-text"
                          :class="{ 'question-view-option-text--correct': isSubQuestionCorrectOption(subQuestion, option) }"
                        >
                          {{ optionIndex + 1 }}.
                        </span>
                        <div
                          class="question-view-option-text"
                          :class="{ 'question-view-option-text--correct': isSubQuestionCorrectOption(subQuestion, option) }"
                          v-html="formatOptionValue(option.optionValue)"
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div class="question-view-summary-grid mt-8" v-if="subQuestion.questionType === 'FILL_IN_THE_BLANKS'">
                    <div>
                      <p class="question-view-label mb-2">Answer</p>
                      <p class="question-view-value mb-0">{{ subQuestion.correctAnswer || "NA" }}</p>
                    </div>
                  </div>

                  <div
                    v-if="subQuestion.questionType === 'MATCH_THE_FOLLOWING' && getSubQuestionMatchingPairs(subQuestion).length"
                    class="question-view-section mt-8"
                  >
                    <div class="question-view-match-header">
                      <p class="question-view-label mb-2">Items</p>
                      <p class="question-view-label mb-2">Matching Pair</p>
                    </div>
                    <div
                      v-for="(pair, pairIndex) in getSubQuestionMatchingPairs(subQuestion)"
                      :key="pairIndex"
                      class="question-view-match-row pb-4"
                    >
                      <p class="question-view-value mb-0">{{ pairIndex + 1 }}. {{ pair.item }}</p>
                      <p class="question-view-value mb-0">{{ pair.match }}</p>
                    </div>
                  </div>

                  <div v-if="subQuestion.answerExplanation" class="mt-8">
                    <p class="question-view-label mb-2">Answer Explanation</p>
                    <div
                      class="question-view-value"
                      v-html="subQuestion.answerExplanation || 'NA'"
                    ></div>
                  </div>

                  <div class="question-view-meta-grid mt-8">
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Hint</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.hint) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Strand</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.strand) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Sub-Strand</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.subStrand) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Topic</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.topic) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">LO</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.selectedLOs) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Case Sensitive</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.isCaseSensitive) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Bloom's Taxonomy Level</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.bloomsTaxonomy) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Difficulty Level</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.difficultyLevel) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Complexity Level</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.complexityLevel) }}</p>
                    </div>
                    <div class="question-view-meta-item">
                      <p class="question-view-label mb-2">Proficiency Level</p>
                      <p class="question-view-value mb-0">{{ getDisplayValue(subQuestion.proficiencyLevel) }}</p>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </template>

        <template v-else-if="!isPassageBased">
          <div class="question-view-section">
            <p class="question-view-label mb-2">Question Type</p>
            <p class="question-view-value mb-0">
              {{ formatQuestionType(questionDetail.questionType) || "NA" }}
            </p>
          </div>

          <div class="question-view-section mt-8">
            <p class="question-view-label mb-2">Question</p>
            <div
              class="question-view-rich-text"
              v-html="formatQuestionStatementForDisplay(questionDetail.questionStatement) || 'NA'"
            ></div>
            <template v-if="resolvedAssetUrl">
              <v-img
                v-if="assetMediaKind(resolvedAssetType) === 'image'"
                :src="resolvedAssetUrl"
                class="question-view-asset mt-4"
                contain
                max-width="360"
              />
              <component
                v-else-if="assetMediaKind(resolvedAssetType) === 'audio' || assetMediaKind(resolvedAssetType) === 'video'"
                :is="assetMediaKind(resolvedAssetType)"
                :src="resolvedAssetUrl"
                controls
                controlsList="nodownload noplaybackrate"
                class="question-view-media mt-4"
              />
            </template>
          </div>

          <div class="question-view-summary-grid mt-8">
            <div>
              <p class="question-view-label mb-2">Marking Type</p>
              <p class="question-view-value mb-0">{{ questionDetail.markingType || "NA" }}</p>
            </div>
            <div>
              <p class="question-view-label mb-2">Correct Answer Score</p>
              <p class="question-view-value mb-0">
                {{ questionDetail.marksAllotted ?? questionDetail.correctAnswerScore ?? "NA" }}
              </p>
            </div>
          </div>

          <div
            v-if="!isMatchTheFollowing && !isFillInTheBlank && resolvedOptions.length"
            class="question-view-section mt-8"
          >
            <p class="question-view-section-title mb-4">Options</p>
            <div class="question-view-options-grid">
              <div
                v-for="(option, index) in resolvedOptions"
                :key="option.optionKey || index"
                class="question-view-option-item"
              >
                <span
                  class="question-view-option-text"
                  :class="{ 'question-view-option-text--correct': isCorrectOption(option) }"
                >
                  {{ index + 1 }}.
                </span>
                <div
                  class="question-view-option-text"
                  :class="{ 'question-view-option-text--correct': isCorrectOption(option) }"
                  v-html="formatOptionValue(option.optionValue)"
                ></div>
              </div>
            </div>
          </div>
          <div class="question-view-summary-grid mt-8" v-if="isFillInTheBlank">
            <div>
              <p class="question-view-label mb-2">Answer</p>
              <p class="question-view-value mb-0">{{ questionDetail.correctAnswer || "NA" }}</p>
            </div>
          </div>

          <div v-if="isMatchTheFollowing && matchingPairs.length" class="question-view-section mt-8">
            <div class="question-view-match-header">
              <p class="question-view-label mb-2">Items</p>
              <p class="question-view-label mb-2">Matching Pair</p>
            </div>
            <div
              v-for="(pair, index) in matchingPairs"
              :key="index"
              class="question-view-match-row pb-4"
            >
              <p class="question-view-value mb-0">{{ index + 1 }}. {{ pair.item }}</p>
              <p class="question-view-value mb-0">{{ pair.match }}</p>
            </div>
          </div>

          <div v-if="questionDetail.answerExplanation" class="mt-8">
            <p class="question-view-label mb-2">Answer Explanation</p>
            <div
              class="question-view-value"
              v-html="questionDetail.answerExplanation || 'NA'"
            ></div>
          </div>

          <div class="question-view-meta-grid mt-8">
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Hint</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.hint) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Application</p>
              <p class="question-view-value mb-0">
                {{ getDisplayValue(questionDetail.application) }}
              </p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Skill</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.skill) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Set Number</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.setNumber) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Subject</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.subject) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">NEP Level</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.level) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Grade</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.grade) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Strand</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.strand) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Sub-Strand</p>
              <p class="question-view-value mb-0">
                {{ getDisplayValue(questionDetail.subStrand) }}
              </p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Topic</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.topic) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">LO</p>
              <p class="question-view-value mb-0">
                {{ getDisplayValue(questionDetail.selectedLOs) }}
              </p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">HRIS Unique Role</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.subStrand) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Language</p>
              <p class="question-view-value mb-0">
                {{ getDisplayValue(questionDetail.languageOfQuestion) }}
              </p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Case Sensitive</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.isCaseSensitive) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Bloom's Taxonomy Level</p>
              <p class="question-view-value mb-0">
                {{ getDisplayValue(questionDetail.bloomsTaxonomy) }}
              </p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Difficulty Level</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.difficultyLevel) }}</p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Complexity Level</p>
              <p class="question-view-value mb-0">
                {{ getDisplayValue(questionDetail.complexityLevel) }}
              </p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Proficiency Level</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.proficiencyLevel) }}</p>
            </div>
              <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Authored By</p>
              <p class="question-view-value mb-0">
                {{ getDisplayValue(questionDetail.authoredBy) }}
              </p>
            </div>
            <div class="question-view-meta-item">
              <p class="question-view-label mb-2">Group</p>
              <p class="question-view-value mb-0">{{ getDisplayValue(questionDetail.groupName) }}</p>
            </div>
          </div>
        </template>
      </div>
    </v-container>
  </div>
</template>

<script>
import QuestionsController from "@/controllers/QuestionsController";
import UserNavMenu from "@/components/UserNavMenu.vue";

export default {
  name: "ViewQuestion",
  components: {
    UserNavMenu,
  },
  data() {
    return {
      isLoading: false,
      routeQuestion: {},
      fetchedQuestion: null,
      resolvedAssetUrl: null,
      resolvedAssetType: null,
      resolvedOptions: [],
      viewQuestionPanels: [],
    };
  },
  computed: {
    questionDetail() {
      return this.fetchedQuestion || this.routeQuestion || {};
    },
    isPassageBased() {
      return this.questionDetail?.questionType === "PASSAGE_BASED";
    },
    isMatchTheFollowing() {
      return this.questionDetail?.questionType === "MATCH_THE_FOLLOWING";
    },
    isFillInTheBlank() {
      return this.questionDetail?.questionType === "FILL_IN_THE_BLANKS";
    },
    matchingPairs() {
      const correctAnswer = this.questionDetail?.correctAnswer;
      if (!correctAnswer || typeof correctAnswer !== "string") return [];
      return correctAnswer.split(",").map((pair) => {
        const colonIndex = pair.indexOf(":");
        if (colonIndex === -1) return null;
        return {
          item: pair.slice(0, colonIndex).trim(),
          match: pair.slice(colonIndex + 1).trim(),
        };
      }).filter(Boolean);
    },
    hasQuestionAsset() {
      const assetUrl = this.questionDetail?.questionAssetUrl;
      return Boolean(
        assetUrl &&
          assetUrl !== "NA" &&
          (assetUrl.startsWith("http://") ||
            assetUrl.startsWith("https://") ||
            assetUrl.startsWith("blob:")),
      );
    },
  },
  watch: {
    "$route.query.id": {
      handler() {
        this.loadQuestion();
      },
      immediate: true,
    },
  },
  methods: {
    toggleViewQuestionPanels(expand) {
      this.viewQuestionPanels = expand
        ? [...Array((this.questionDetail.subQuestions || []).length).keys()]
        : [];
    },
    assetMediaKind(assetType) {
      if (!assetType) return 'image';
      switch (assetType) {
        case 'audio': return 'audio';
        case 'video': return 'video';
        default: return 'image';
      }
    },
    async loadQuestion() {
      this.routeQuestion = this.parseRouteQuestion();
      this.fetchedQuestion = null;
      this.resolvedAssetUrl = null;
      this.resolvedAssetType = null;
      this.resolvedOptions = [];

      const questionId = this.$route.query.id;
      if (!questionId) {
        await this.resolveMediaUrls(this.routeQuestion);
        return;
      }

      this.isLoading = true;

      try {
        const response = await QuestionsController.getQuestionByQuestionId(questionId);

        if (response?.status === 200) {
          this.fetchedQuestion =
            response.data?.data?.question ||
            response.data?.question ||
            response.data?.data ||
            response.data ||
            this.routeQuestion;
        }
      } catch (error) {
        console.error("Error fetching question details:", error);
      } finally {
        this.isLoading = false;
        await this.resolveMediaUrls(this.questionDetail);
      }
    },
    async resolveMediaUrls(question) {
      if (!question) return;

      const assetUrl = question.questionAssetUrl;
      if (assetUrl && assetUrl !== 'NA') {
        this.resolvedAssetUrl = await this.resolveDriveUrl(assetUrl);
        this.resolvedAssetType = question.questionAssetType || null;
      }

      const options = Array.isArray(question.questionOptions) ? question.questionOptions : [];
      this.resolvedOptions = await Promise.all(
        options.map(async (opt) => {
          if (opt.optionValue && /^https?:\/\//i.test(opt.optionValue)) {
            return { ...opt, optionValue: await this.resolveDriveUrl(opt.optionValue) };
          }
          return opt;
        })
      );
    },
    async resolveDriveUrl(url) {
      if (!url || !url.startsWith('https://drive.google.com/')) return url;
      let fileId = null;
      if (url.indexOf('&id=') > -1) {
        fileId = url.split('&id=').pop();
      } else {
        const match = url.match(/\/file\/d\/([^/?]+)/);
        if (match) fileId = match[1];
      }
      if (!fileId) return url;
      try {
        const response = await QuestionsController.doFetchFileContentFromDriveUrl(fileId);
        if (response.status === 200) {
          return window.URL.createObjectURL(new Blob([response.data]));
        }
      } catch (e) {
        console.error('Error resolving Drive URL:', e);
      }
      return url;
    },
    parseRouteQuestion() {
      try {
        return this.$route.query.question
          ? JSON.parse(this.$route.query.question)
          : {};
      } catch (error) {
        return {};
      }
    },
    formatQuestionType(value) {
      if (!value) return "";
      return value
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    formatQuestionStatementForDisplay(statement) {
      if (!statement) return statement;
      return statement.replace(/\{\s*blank\s*\}/gi, "__________");
    },
    getCorrectAnswerKeys() {
      const correctAnswer = this.questionDetail?.correctAnswer;

      if (Array.isArray(correctAnswer)) {
        return correctAnswer.map((value) => String(value).trim()).filter(Boolean);
      }

      if (typeof correctAnswer === "string") {
        return correctAnswer.split(",").map((value) => value.trim()).filter(Boolean);
      }

      if (correctAnswer === null || correctAnswer === undefined) {
        return [];
      }

      return [String(correctAnswer).trim()].filter(Boolean);
    },
    isCorrectOption(option) {
      const optionKey = option?.optionKey ? String(option.optionKey).trim() : "";
      return optionKey ? this.getCorrectAnswerKeys().includes(optionKey) : false;
    },
    getSubQuestionCorrectAnswerKeys(subQuestion) {
      const correctAnswer = subQuestion?.correctAnswer;

      if (Array.isArray(correctAnswer)) {
        return correctAnswer.map((value) => String(value).trim()).filter(Boolean);
      }

      if (typeof correctAnswer === "string") {
        return correctAnswer.split(",").map((value) => value.trim()).filter(Boolean);
      }

      if (correctAnswer === null || correctAnswer === undefined) {
        return [];
      }

      return [String(correctAnswer).trim()].filter(Boolean);
    },
    isSubQuestionCorrectOption(subQuestion, option) {
      const optionKey = option?.optionKey ? String(option.optionKey).trim() : "";
      return optionKey ? this.getSubQuestionCorrectAnswerKeys(subQuestion).includes(optionKey) : false;
    },
    getSubQuestionResolvedOptions(subQuestion) {
      return Array.isArray(subQuestion?.questionOptions) ? subQuestion.questionOptions : [];
    },
    getSubQuestionMatchingPairs(subQuestion) {
      const correctAnswer = subQuestion?.correctAnswer;
      if (!correctAnswer || typeof correctAnswer !== "string") return [];
      return correctAnswer.split(",").map((pair) => {
        const colonIndex = pair.indexOf(":");
        if (colonIndex === -1) return null;
        return {
          item: pair.slice(0, colonIndex).trim(),
          match: pair.slice(colonIndex + 1).trim(),
        };
      }).filter(Boolean);
    },
    getDisplayValue(value) {
      if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "NA";
      }

      return value || "NA";
    },
    resolveImageUrl(url) {
      if (!url || url === 'NA') return url;
      const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/?]+)/);
      if (driveMatch) {
        return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
      }
      return url;
    },
    formatOptionValue(value) {
      if (!value) return 'NA';
      if (/^(https?:\/\/|blob:)/i.test(value.trim())) {
        return `<img src="${this.resolveImageUrl(value.trim())}" style="max-width:200px; max-height:150px; object-fit:contain;" />`;
      }
      return value;
    },
  },
};
</script>

<style scoped>
.question-view-page {
  padding-top: 96px !important;
  padding-bottom: 32px !important;
}

.question-view-card {
  background: #ffffff;
  border: 1px solid #d9dee8;
  border-radius: 14px;
  min-height: calc(100vh - 144px);
}

.question-view-loading {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #4a4a4a;
  font-size: 14px;
  font-weight: 500;
}

.question-view-label {
  font-size: 12px;
  font-weight: 400;
  color: #7d7d7d;
}

.question-view-expansion-panel {
  border-radius: 10px;
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.08) !important;
}

.question-view-expansion-header {
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08) !important;
}

.question-view-expansion-panel:not(:first-child) {
  margin-top: 16px;
}

.question-view-value,
.question-view-rich-text,
.question-view-option-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  color: #4a4a4a;
}

.question-view-section-title {
  font-size: 16px;
  font-weight: 700;
  color: #2f2f2f;
}

.question-view-summary-grid,
.question-view-meta-grid,
.question-view-options-grid {
  display: grid;
  gap: 24px;
}

.question-view-summary-grid,
.question-view-meta-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.question-view-options-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.question-view-option-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.question-view-option-text--correct {
  color: #19b56b;
}

.question-view-highlight {
  padding: 14px 16px;
  border-radius: 10px;
  background: #fde8de;
}

.question-view-asset {
  border-radius: 10px;
}

.question-view-media {
  display: block;
  width: 100%;
  max-width: 360px;
  border-radius: 10px;
}

.question-view-match-header,
.question-view-match-row {
  display: flex;
  gap: 24px;
}

.question-view-match-header > p,
.question-view-match-row > p {
  flex: 1;
}

@media (max-width: 960px) {
  .question-view-summary-grid,
  .question-view-meta-grid,
  .question-view-options-grid {
    grid-template-columns: 1fr;
  }
}
</style>
